/**
 * One world buffer's screen-space reflection, which lives as long as the buffer: its buffers and
 * the camera of its last frame. Each trace reads this frame's prepass and the last frame's colour,
 * and the end of each render copies the finished frame for the next trace
 * (`render_forward_clustered.cpp:2413-2417`).
 */
import * as THREE from 'three';
import { beginStep, endStep } from '../frameProfile/frameProfile.js';
import { leaveSlot } from '../sharedTexture.js';
import { FittedBuffers } from '../screenSpaceEffects/fittedBuffers.js';
import { ReflectionBuffers } from './reflectionBuffers.js';
import { copyLastFrame, traceReflection } from './reflectionPasses.js';
import { ReflectionFrames } from './reflectionFrames.js';
import { reflectionSlot } from './ssrUniforms.js';
/**
 * Godot's `set_depth_correction(true)`: y flipped, so screen rows run top-down, and depth reversed
 * into 0 to 1 (`projection.cpp`, `ss_effects.cpp:1489`).
 */
const DEPTH_CORRECTION = new THREE.Matrix4().set(1, 0, 0, 0, 0, -1, 0, 0, 0, 0, -0.5, 0.5, 0, 0, 0, 1);
/** Scratch for one trace. */
const projection = new THREE.Matrix4();
const inverseProjection = new THREE.Matrix4();
const reprojection = new THREE.Matrix4();
export class WorldReflection {
    buffers = new FittedBuffers((renderer, shape) => new ReflectionBuffers(renderer, shape), (renderer, buffers) => leaveSlot(renderer, reflectionSlot, buffers.reflection.texture));
    /** Godot starts both at identity, over a last frame cleared to zero (`ss_effects.cpp:392`). */
    lastProjection = new THREE.Matrix4();
    lastView = new THREE.Matrix4();
    frames = new ReflectionFrames();
    /** The reflection of the last trace, as the colour pass reads it. Null before the first. */
    get reflection() {
        return this.buffers.current?.reflection.texture ?? null;
    }
    /** Traces the reflection of the frame `prepass` holds, seen through `camera`, at `settings`. */
    trace(renderer, prepass, camera, { ssr, isHalfSize }) {
        const { width, height } = prepass.shape;
        const buffers = this.buffers.fit(renderer, { width, height, isHalfSize });
        projection.multiplyMatrices(DEPTH_CORRECTION, camera.projectionMatrix);
        inverseProjection.copy(projection).invert();
        const inputs = {
            projection,
            inverseProjection,
            reprojection: this.reprojectionOf(camera),
            isOrthogonal: camera.isOrthographicCamera === true,
            ssr,
        };
        beginStep(renderer, 'Screen-space reflection');
        traceReflection(renderer, prepass, buffers, inputs);
        endStep(renderer, 'Screen-space reflection');
    }
    /**
     * Copies the finished `frame` for the next trace, and is true when the preview should draw one
     * more frame to reflect it. The caller restores the renderer's target.
     */
    finish(renderer, frame) {
        copyLastFrame(renderer, this.buffers.current, frame);
        return this.frames.traced();
    }
    release(renderer) {
        this.buffers.release(renderer);
    }
    /**
     * `last_projection * last_view * camera_world * projection^-1` (`render_forward_clustered.cpp:1469-1476`),
     * after which this frame's camera becomes the last frame's.
     */
    reprojectionOf(camera) {
        reprojection
            .multiplyMatrices(this.lastProjection, this.lastView)
            .multiply(camera.matrixWorld)
            .multiply(inverseProjection);
        this.lastProjection.copy(projection);
        this.lastView.copy(camera.matrixWorldInverse);
        return reprojection;
    }
}
//# sourceMappingURL=worldReflection.js.map